import React, { useEffect, useState } from 'react';
import { Table, Badge, Button, Input, FormGroup, Label } from 'reactstrap';
import { Link, useHistory } from 'react-router-dom';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import FormCard from '../../shared/components/custom/FormCard';
import SquareIconButton from '../../shared/components/custom/SquareIconButton';
import { toast } from 'react-toastify';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import moment from 'moment';
import DragIcon from 'mdi-react/DragIcon';
import skeletonTableLoader from '../../shared/components/custom/SkeletonLoaders';
import t, { partial } from '../../util/translation/translation';
import { getBaseDate } from '../../util/date-and-time';
import Api from '../../util/api';
import DeleteModal from '../../shared/components/custom/DeleteModal';
import EmptyStateInfo from '../../shared/components/custom/EmptyStateInfo';
import useModal from '../../shared/hooks/useModal';
import SpotlightPhonePreview from './components/SpotlightPhonePreview';
import SpotlightStoryPreview, {
  SpotlightStoriesRow,
} from './components/SpotlightStoryPreview';
import SpotlightTakeoverPreview from './components/SpotlightTakeoverPreview';
import SpotlightTakeoverGenericPreview from './components/SpotlightTakeoverGenericPreview';
import { isGenericTakeover } from './takeoverUtils';

const UNLIMITED_DATE = '9999-12-31T00:00:00.000Z';
const TileListPage = ({ groupId, group, setLoading }) => {
  const history = useHistory();
  const fp = partial('HighlightBannerFormPage');
  const [handleModal, visible, deleteId] = useModal();
  const [tiles, setTiles] = useState([]);
  const [filterExpired, setExpiredFilter] = useState(true);
  const s = partial('shared');
  const p = partial('HighlightBannerPage');
  const fetchTiles = async () => {
    setLoading(true);
    try {
      const tileData = await Api.spotlight.getTilesForGroup(groupId);
      const sortedTiles = tileData.sort(
        (a, b) => Number(a.order.split('@')[1]) - Number(b.order.split('@')[1])
      );
      setTiles(sortedTiles);
      const spotlightIds = tileData.map((item) => item.id);
      const { data: pressData } = await Api.spotlight.getSpotlightPressAmount({
        spotlightIds,
      });
      const sorted = sortedTiles.map((item) => ({
        ...item,
        presses:
          pressData.find((spotlightPress) => spotlightPress.itemId === item.id)
            ?.presses || 0,
      }));
      setTiles(sorted);
      setLoading(false);
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
      setLoading(false);
    }
  };
  useEffect(() => {
    if (groupId) {
      fetchTiles();
    }
  }, [groupId]);
  const handleDeleteTile = async () => {
    setLoading(true);
    try {
      await Api.spotlight.deleteTile(deleteId);
      const tileData = await Api.spotlight.getTilesForGroup(groupId);
      const sorted = tileData.sort(
        (a, b) => Number(a.order.split('@')[1]) - Number(b.order.split('@')[1])
      );
      handleModal();
      setTiles(sorted);
      setLoading(false);
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
      setLoading(false);
    }
  };

  const StateBadge = ({ state }) => {
    switch (state) {
      case 'active':
        return <Badge color={'success'}>{s('active')}</Badge>;
      case 'expired':
        return <Badge color={'secondary'}>{s('Expired')}</Badge>;
      case 'scheduled':
        return <Badge color={'warning'}>{s('scheduled')}</Badge>;
      default:
        return <Badge color={'disabled'}>{s('inActive')}</Badge>;
    }
  };

  const renderTiles = (filterExpired) => {
    // Extract filtering logic to reuse in onDragEnd
    const filteredTiles = tiles.filter((x) =>
      filterExpired ? !moment().isAfter(moment(x.expirationDate)) : true
    );

    return filteredTiles.map((n, index) => {
      const isActive = moment().isBetween(
        moment(n.publicationDate),
        moment(n.expirationDate)
      );

      const isNotStarted = moment().isBefore(moment(n.publicationDate));

      const isExpired = moment().isAfter(moment(n.expirationDate));

      let badgeStatus;
      switch (true) {
        case isActive:
          badgeStatus = 'active';
          break;
        case isNotStarted:
          badgeStatus = 'scheduled';
          break;
        case isExpired:
          badgeStatus = 'expired';
          break;
        default:
          badgeStatus = '';
      }

      return (
        <Draggable
          key={`tile-${n.id}`}
          draggableId={`tile-${n.id}`}
          index={index}
        >
          {(provided, snapshot) => (
            <tr
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              className={snapshot.isDragging ? 'rbdnd-dragging' : ''}
              key={`tile-${n.id}`}
            >
              {/* Drag handle only: the order is the row's position */}
              <td>
                <DragIcon color="#99999" />
              </td>
              <td className="table-stack__primary">
                <Link to={`/spotlight/group/edit/${groupId}/tile/edit/${n.id}`}>
                  {n.title}
                </Link>
              </td>
              <td data-label={s('publicationDate')}>
                {getBaseDate(n.publicationDate)}
              </td>
              <td data-label={s('expirationDate')}>
                {n.expirationDate !== UNLIMITED_DATE
                  ? getBaseDate(n.expirationDate)
                  : '-'}
              </td>
              <td data-label={s('status')}>
                <StateBadge state={badgeStatus} />
              </td>
              <td data-label={s('clicks')}>
                {n.presses === undefined
                  ? skeletonTableLoader(1, 1, 50)
                  : n.presses}
              </td>
              <td className="table-stack__actions">
                <div className="icon-actions">
                  <SquareIconButton
                    icon={<EditOutlineIcon />}
                    onClick={() =>
                      history.push(
                        `/spotlight/group/edit/${groupId}/tile/edit/${n.id}`
                      )
                    }
                    title={s('edit')}
                    id={`edit-tile-${n.id}`}
                  />
                  <SquareIconButton
                    icon={<DeleteOutlineIcon />}
                    onClick={() => handleModal(n.id)}
                    title={s('delete')}
                    tone="danger"
                    id={`delete-tile-${n.id}`}
                  />
                </div>
              </td>
            </tr>
          )}
        </Draggable>
      );
    });
  };

  const onDragEnd = async ({ destination, source }) => {
    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Work with filtered tiles for drag operations
    const filteredTiles = tiles.filter((x) =>
      filterExpired ? !moment().isAfter(moment(x.expirationDate)) : true
    );

    const draggedTile = filteredTiles[source.index];
    const filteredCopy = [...filteredTiles];
    filteredCopy.splice(source.index, 1);
    filteredCopy.splice(destination.index, 0, draggedTile);

    // Get hidden tiles (tiles not in the filtered view)
    const hiddenTiles = tiles.filter((x) =>
      filterExpired ? moment().isAfter(moment(x.expirationDate)) : false
    );

    // Reorder all tiles properly
    const reorderedFilteredTiles = filteredCopy.map((item, idx) => ({
      ...item,
      order: `${item.order.split('@')[0]}@${idx}`,
    }));

    // Assign order values to hidden tiles starting after the visible ones
    const reorderedHiddenTiles = hiddenTiles.map((item, idx) => ({
      ...item,
      order: `${item.order.split('@')[0]}@${filteredCopy.length + idx}`,
    }));

    // Combine and sort all tiles by their new order
    const allReorderedTiles = [
      ...reorderedFilteredTiles,
      ...reorderedHiddenTiles,
    ];
    const finalOrderedTiles = allReorderedTiles.sort(
      (a, b) => Number(a.order.split('@')[1]) - Number(b.order.split('@')[1])
    );

    // Update state immediately
    setTiles(finalOrderedTiles);

    // Send only the filtered tiles order to the API
    const orderedIds = reorderedFilteredTiles.map((item) => item.id);

    setLoading(true);
    try {
      await Api.spotlight.updateOrder(groupId, { tileIds: orderedIds });
      toast.success(s('Toast.successfullEdit'));
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(p('errorUpdatingOrder'));
      // Revert state on error by refetching
      fetchTiles();
    }
  };
  // What users see in the app right now: published, not expired, in order
  const liveTiles = tiles.filter(
    (tile) =>
      moment(tile.publicationDate).isSameOrBefore(moment()) &&
      (tile.expirationDate === UNLIMITED_DATE ||
        !tile.expirationDate ||
        moment(tile.expirationDate).isAfter(moment()))
  );
  // Live tiles as the previews take them
  const previewTiles = liveTiles.map((tile) => ({
    key: tile.id,
    tile: {
      title: tile.title,
      description: tile.description,
      highlight: tile.highlight,
      headingImage: {
        preview: tile.imageUrl,
        croppedImage: tile.imageUrl,
      },
      content: { value: tile.contentType },
      contentType: tile.contentId,
      presentation: tile.presentation,
    },
  }));

  return (
    <>
      <DeleteModal
        visible={visible}
        handleModal={handleModal}
        type="tile"
        modalAction={handleDeleteTile}
      />
      {/* Side by side when both fit, otherwise the preview below the table */}
      <div className="spot-tiles">
        <FormCard
          title={fp('tiles')}
          actions={
            <div className="d-flex align-items-center" style={{ gap: 16 }}>
              <FormGroup check inline className="mb-0">
                <Input
                  id="tiles-show-expired"
                  type="checkbox"
                  className="my-auto"
                  onChange={() => setExpiredFilter(!filterExpired)}
                  style={{ height: '15px', width: '15px' }}
                  checked={!filterExpired}
                />
                <Label check for="tiles-show-expired">
                  {s('ShowExpired')}
                </Label>
              </FormGroup>
              <Link to={`/spotlight/group/edit/${groupId}/tile/create`}>
                <Button size="sm" color="primary" className="mb-0">
                  {s('create')}
                </Button>
              </Link>
            </div>
          }
          bodyClassName="form-card__body--flush"
          className="spot-tiles__table"
        >
          {tiles?.length > 0 && (
            <DragDropContext onDragEnd={onDragEnd}>
              <Table responsive striped className="table--roomy table--stack">
                <thead>
                  <tr>
                    <th />
                    <th>
                      <div className="flex alignItems-center">
                        {s('heading')}
                      </div>
                    </th>
                    <th>
                      <div className="flex alignItems-center">
                        {s('publicationDate')}
                      </div>
                    </th>
                    <th>
                      <div className="flex alignItems-center">
                        {s('expirationDate')}
                      </div>
                    </th>
                    <th>{s('status')}</th>
                    <th>{s('clicks')}</th>
                    <th />
                  </tr>
                </thead>
                <Droppable droppableId="group-dnd">
                  {(provided) => {
                    return (
                      <>
                        <tbody
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          {renderTiles(filterExpired)}
                          {provided.placeholder}
                        </tbody>
                      </>
                    );
                  }}
                </Droppable>
              </Table>
            </DragDropContext>
          )}
          {tiles?.length === 0 && (
            <div className="mb-4">
              <EmptyStateInfo
                icon="layout"
                url={`/spotlight/group/edit/${groupId}/tile/create`}
                linkName={p('createTile')}
                title={p('createTitle')}
                text={p('createText')}
              />
            </div>
          )}
        </FormCard>
        {group && (
          <FormCard
            title={t('NewsForm.appPreview')}
            actions={<span>{p('previewLiveHint')}</span>}
            className="spot-tiles__preview"
          >
            {group.format === 'TAKEOVER' ? (
              // Takeovers open one at a time, the first unseen one first
              previewTiles.length ? (
                <div className="story-group-preview">
                  <span className="story-group-preview__label">
                    {t('HighlightBannerForm.takeoverOrder', {
                      count: previewTiles.length,
                    })}
                  </span>
                  <div className="story-group-preview__slides takeover-preview-list">
                    {/* Each in its own layout */}
                    {previewTiles.map((item) =>
                      isGenericTakeover(item.tile?.presentation) ? (
                        <SpotlightTakeoverGenericPreview
                          key={item.key}
                          tile={item.tile}
                          compact
                        />
                      ) : (
                        <SpotlightTakeoverPreview
                          key={item.key}
                          tile={item.tile}
                          compact
                        />
                      )
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-muted mb-0">{p('previewNoLiveTiles')}</p>
              )
            ) : group.format === 'STORY' ? (
              // A story: its bubble on the home screen, then its slides
              previewTiles.length ? (
                <div className="story-group-preview">
                  <SpotlightStoriesRow group={group} tiles={previewTiles} />
                  <span className="story-group-preview__label">
                    {t('HighlightBannerForm.storySlides', {
                      count: previewTiles.length,
                    })}
                  </span>
                  <div className="story-group-preview__slides">
                    {previewTiles.map((item, index) => (
                      <SpotlightStoryPreview
                        key={item.key}
                        group={group}
                        tile={item.tile}
                        index={index}
                        count={previewTiles.length}
                        compact
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-muted mb-0">{p('previewNoLiveTiles')}</p>
              )
            ) : (
              <SpotlightPhonePreview
                group={group}
                tiles={previewTiles}
                emptyText={p('previewNoLiveTiles')}
              />
            )}
          </FormCard>
        )}
      </div>
    </>
  );
};

export default TileListPage;
